import * as THREE from 'three';
import type { WorldSnapshot } from '../../../shared/network/protocol.js';
import type { UnitState } from '../../../shared/game/world-types.js';
import { disposeEffect, effectMaterial, effectParticles, pathRibbon, shadeEffect, spiralRibbon } from './effect-surfaces.js';
import { distortionShell } from './howl-wave.js';
import { softGlowTexture } from './dark-smoke.js';
import { SHIVER } from './sneak-shimmer.js';

/**
 * `withered`: Withering Touch's round as undead. True Strike lights the guided hero's weapon instead (`shepherd-effects.ts`). The Mage's:
 * `slowed` (Ice Lance), `blinded` (Snowblind) and `charged` (any lightning damage); Smoldering is a glow of the body itself (`mage-effects.ts`).
 */
export type StatusKind = 'dazed' | 'prone' | 'frightened' | 'hidden' | 'running' | 'withered' | 'slowed' | 'blinded' | 'charged';
const STATUS_KINDS: readonly StatusKind[] = ['dazed', 'prone', 'frightened', 'hidden', 'running', 'withered', 'slowed', 'blinded', 'charged'];
/** Condition captions whose look is the persistent status cue rather than a one-shot burst. */
export const persistentStatusLabels: ReadonlyMap<string, StatusKind> = new Map([['DAZED', 'dazed'], ['PRONE', 'prone'], ['FRIGHTENED', 'frightened'], ['WITHERED', 'withered'],
  ['SLOWED', 'slowed'], ['BLIND', 'blinded'], ['CHARGED', 'charged']]);
/** A hidden hero's shimmer: the air round its shadowed figure bends faintly, wavering with it (the figure's darkening is `shadow-figures.ts`). */
const SHIMMER_TINT = 0xb9c1cf;
const TAU = Math.PI * 2, DAZE = 0xcdb98c, DAZE_LIGHT = 0xfff1d6, DUST = 0xbaa484, VIOLET = 0xb28cff;
/** The Necrotic school's colours (`content/spells/schools.ts`). */
const ROT = 0x4a9a3a, ROT_LIGHT = 0x9fe08a;
/** Puffs of fog round a Blind head. */
const BLIND_FOG = 12, CHARGED_SPARKS = 5, SPARK_FROM = new THREE.Vector3(), SPARK_TO = new THREE.Vector3();
/** Fire, Ice and Lightning (`content/spells/effects.ts` `mageEffects`). */
const FROST = 0x8fd6ff, FROST_LIGHT = 0xd8f2ff, STORM = 0x6a7bff, STORM_LIGHT = 0xbfd0ff;

/** Which persistent statuses the authoritative state holds on a unit. */
export function unitStatuses(unit: Pick<UnitState, 'prone' | 'frightenedBy' | 'combat'> & { hidden?: boolean }): StatusKind[] {
  const statuses: StatusKind[] = [];
  if (unit.hidden) statuses.push('hidden');
  if (unit.combat.running) statuses.push('running');
  if (unit.combat.dazed || unit.combat.rooted) statuses.push('dazed');
  if (unit.prone) statuses.push('prone');
  if (unit.frightenedBy?.length) statuses.push('frightened');
  if (unit.combat.withered) statuses.push('withered');
  if (unit.combat.slowed) statuses.push('slowed');
  if (unit.combat.blinded) statuses.push('blinded');
  if (unit.combat.charged) statuses.push('charged');
  return statuses;
}

// Mode 4 draws a bright core along the ribbon; the default ribbon shading is too faint to hold a status at game-camera distance.
function ribbon(parent: THREE.Object3D, radius: number, height: number, color: number, phase: number, turns: number, width: number, opacity = 1, accent = 0xfff3ce) {
  const mesh = new THREE.Mesh(spiralRibbon(radius, height, turns, width, phase), effectMaterial(color, accent, 4));
  mesh.userData.baseOpacity = opacity; parent.add(mesh); return mesh;
}

/**
 * A dim, reeling swirl of trails and motes round a Dazed brow; a low dust ring under a Prone unit; violet wisps winding round a Frightened one;
 * a faint bending of the air round a Hidden hero; a pair of quick, low dust trails scuffing round the feet of a Running one.
 */
export function createStatusVisual(kind: StatusKind, radius: number, height: number) {
  const group = new THREE.Group(); group.name = `status-${kind}`;
  const spin = new THREE.Group(); spin.name = 'status-spin'; group.add(spin);
  if (kind === 'hidden') {
    const shell = distortionShell('hidden-shimmer', SHIVER, SHIMMER_TINT, 2.5);
    shell.mesh.position.y = height * .5; shell.mesh.scale.set(radius * 1.15, height * .56, radius * 1.15);
    group.userData.shimmer = shell.uniforms; spin.add(shell.mesh);
  } else if (kind === 'running') {
    // The dust-coloured counterpart of the hidden shimmer: low, quick streaks hugging the ground instead of air bending round the figure.
    for (let i = 0; i < 2; i++) ribbon(spin, radius * (1 + i * .25), .03, DUST, i * Math.PI, .5, .08, .5 - i * .15, 0xe6d9bd);
    spin.add(effectParticles(DUST, 6, radius * 1.2, .2));
  } else if (kind === 'dazed') {
    // Muted, unsaturated light in the painted style rather than cartoon stars: short trails chasing each other round the head.
    for (let i = 0; i < 3; i++) { const trail = ribbon(spin, .3 + i * .03, .05, DAZE, i * TAU / 3, .32, .06, .85, DAZE_LIGHT); trail.name = 'status-trail'; }
    spin.add(effectParticles(DAZE_LIGHT, 7, .34, .12, 1));
  } else if (kind === 'prone') {
    for (let i = 0; i < 2; i++) ribbon(spin, radius * (1.15 + i * .18), .04, DUST, i * Math.PI, .8, .1, .45 - i * .12);
    spin.add(effectParticles(DUST, 7, radius * 1.3, .28));
  } else if (kind === 'withered') {
    // Thin threads of rot crawling up the ashen body (the tint is `shepherd-effects.ts`), with spores lifting off it.
    for (let i = 0; i < 2; i++) ribbon(spin, radius * (.6 + i * .08), height * .75, ROT, i * Math.PI, .9, .035, .5, ROT_LIGHT);
    spin.add(effectParticles(ROT_LIGHT, 12, radius * .7, height * .85));
  } else if (kind === 'slowed') {
    // Frost about the legs: a rime ring on the ground and pale crystals turning slowly round the shins.
    for (let i = 0; i < 2; i++) ribbon(spin, radius * (1 + i * .12), .03, FROST, i * Math.PI, .9, .07, .55 - i * .15, FROST_LIGHT);
    ribbon(spin, radius * .8, height * .3, FROST_LIGHT, 0, .6, .035, .4, 0xffffff);
    spin.add(effectParticles(FROST_LIGHT, 10, radius * .9, height * .3));
  } else if (kind === 'blinded') {
    // A cold fog wrapped round the head, thick enough to read from the game's camera, with a band of frost at the eyes and flakes falling from it.
    const fog = Array.from({ length: BLIND_FOG }, (_, i) => {
      const puff = new THREE.Sprite(new THREE.SpriteMaterial({ map: softGlowTexture(), color: i % 3 ? 0xd6e6f2 : 0xf4f9ff, transparent: true, depthWrite: false, opacity: 0 }));
      puff.userData.seed = i; spin.add(puff); return puff;
    });
    // A cold light in the fog, so it still reads over dark ground and at night.
    const chill = new THREE.Sprite(new THREE.SpriteMaterial({ map: softGlowTexture(), color: FROST, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, opacity: 0 }));
    chill.scale.setScalar(.75); group.add(chill);
    group.userData.fog = fog; group.userData.chill = chill;
    for (let i = 0; i < 2; i++) ribbon(spin, .12 + i * .02, .03, FROST_LIGHT, i * Math.PI, 1.05, .1 - i * .03, .7, 0xffffff);
    spin.add(effectParticles(0xffffff, 14, .2, -.45));
  } else if (kind === 'charged') {
    // Sparks crawling over the body: short, jagged threads of the Lightning school's light that jump from place to place on it.
    const sparks = Array.from({ length: CHARGED_SPARKS }, () => { const path = pathRibbon(STORM, .035, 4, STORM_LIGHT, 8); path.mesh.userData.baseOpacity = .9; spin.add(path.mesh); return path; });
    group.userData.sparks = sparks; group.userData.body = { radius, height };
    spin.add(effectParticles(STORM_LIGHT, 8, radius * .7, height * .8, 1));
  } else {
    for (let i = 0; i < 3; i++) ribbon(spin, radius * (.8 + i * .08), height * .75, VIOLET, i * TAU / 3, .55, .06, .5);
    spin.add(effectParticles(0xd8c7ff, 6, radius * .9, height * .7));
  }
  return group;
}

/**
 * Poses a status cue `age` seconds after it began: brighter and larger at the moment it lands, then a calm loop.
 * `fade` runs from 1 to 0 once the state has ended, shrinking and dimming the cue away rather than cutting it.
 */
export function updateStatusVisual(group: THREE.Group, kind: StatusKind, age: number, time: number, ppm = 40, fade = 1) {
  const spin = group.getObjectByName('status-spin')!;
  if (kind === 'hidden') {
    // Comes up with the hiding and wavers unevenly with the figure's flicker; spotted, it fades with the cue.
    const shimmer = group.userData.shimmer as { uTime: { value: number }; uRipple: { value: number }; uIor: { value: number }; uThickness: { value: number } };
    const waver = .7 + Math.sin(time * 9.7) * .2 + Math.sin(time * 23.3 + 1.1) * .1, strength = Math.min(1, age / .4) * fade * waver;
    shimmer.uTime.value = time; shimmer.uRipple.value = strength;
    shimmer.uIor.value = 1 + .025 * strength; shimmer.uThickness.value = .2 * strength;
    spin.visible = strength > .002;
    return;
  }
  if (kind === 'running') {
    spin.rotation.set(0, time * -3, 0); spin.scale.setScalar((1 + Math.exp(-age * 4) * .4) * (.55 + .45 * fade));
    shadeEffect(group, time, (.5 + Math.sin(time * 7) * .1) * fade, (time * .9) % 1, ppm);
    return;
  }
  // No fade-in ramp: a status first seen on a paused frame (a join, or the scenario's pause) must still show.
  const onset = Math.exp(-age * 4);
  if (kind === 'dazed') {
    // A slow reel with a drifting tilt, like a head that will not settle.
    spin.rotation.set(Math.sin(time * 1.3) * .32, time * 1.7, Math.cos(time * 1.1) * .32);
    spin.position.y = Math.sin(time * 2.1) * .03 + (1 - fade) * .15;
  } else if (kind === 'blinded') {
    spin.rotation.set(0, time * .7, 0);
    // The fog churns slowly round the head, each puff swelling and thinning on its own breath.
    for (const puff of group.userData.fog as THREE.Sprite[]) {
      const i = puff.userData.seed as number, a = i / BLIND_FOG * Math.PI * 2 + Math.sin(time * .9 + i) * .3, r = .11 + (i % 3) * .035;
      puff.position.set(Math.cos(a) * r, (i % 3 - 1) * .06 + Math.sin(time * 1.3 + i * 2) * .02, Math.sin(a) * r);
      puff.scale.setScalar(.48 + (i % 4) * .07 + Math.sin(time * 1.7 + i * 1.3) * .05);
      puff.material.opacity = (.78 + Math.sin(time * 2.1 + i) * .1) * fade * Math.min(1, .4 + age * 3);
    }
    (group.userData.chill as THREE.Sprite).material.opacity = (.4 + Math.sin(time * 1.4) * .06) * fade;
  } else if (kind === 'charged') {
    // The sparks never sit still: each thread jumps to a new place on the body many times a second, and some go dark between jumps.
    const sparks = group.userData.sparks as ReturnType<typeof pathRibbon>[], { radius, height } = group.userData.body as { radius: number; height: number };
    sparks.forEach((spark, i) => {
      const jump = Math.floor(time * 13 + i * .37), seed = Math.sin(jump * 12.9898 + i * 78.233) * 43758.5453, r = seed - Math.floor(seed);
      const angle = r * TAU, y = height * (.2 + ((r * 7.31) % 1) * .5), reach = .14 + ((r * 3.7) % 1) * .16, out = radius * .62;
      spark.mesh.visible = (r * 11.3) % 1 > .3;
      SPARK_FROM.set(Math.cos(angle) * out, y, Math.sin(angle) * out);
      SPARK_TO.set(Math.cos(angle + reach / out) * out, y + (r > .5 ? reach : -reach) * .8, Math.sin(angle + reach / out) * out);
      spark.update(SPARK_FROM, SPARK_TO, time, true, i, .6);
    });
  } else spin.rotation.set(0, time * (kind === 'prone' ? .25 : kind === 'slowed' ? .15 : -.6), 0);
  spin.scale.setScalar((1 + onset * .45) * (.55 + .45 * fade));
  const loop = kind === 'prone' ? .45 + Math.sin(time * 1.4) * .08 : kind === 'frightened' ? .4 + Math.sin(time * 2.2) * .1
    : kind === 'withered' ? .4 + Math.sin(time * 1.7) * .1
    : kind === 'slowed' ? .5 + Math.sin(time * 1.1) * .06 : kind === 'blinded' ? .55 : kind === 'charged' ? (Math.sin(time * 61) > -.2 ? .75 : .15) : .7;
  const opacity = (loop + onset * .6) * fade;
  shadeEffect(group, time, opacity, (time * .45) % 1, ppm);
}

type ActiveStatus = { kind: StatusKind; unitId: string; group: THREE.Group; started: number; ended?: number; lift: number };
/** Game seconds a status cue takes to fade once its state ends on a unit that is still in view. */
const FADE_OUT = .45;

/** Persistent status cues, drawn for exactly as long as snapshots hold the state; presentation only. */
export class StatusEffects {
  private active = new Map<string, ActiveStatus>();
  /** Effect bench: statuses shown on a unit until a game time, without any server state. */
  private previews = new Map<string, number>();
  get visualObjects(): THREE.Object3D[] { return [...this.active.values()].map(status => status.group); }
  constructor(private scene: THREE.Scene, private heights: ReadonlyMap<string, number>) {}
  preview(unitId: string, kind: StatusKind, until: number) { this.previews.set(`${unitId}:${kind}`, until); }
  update(snapshot: WorldSnapshot, time: number, positions: ReadonlyMap<string, THREE.Object3D>, ppm = 40) {
    const held = new Set<string>(), shown = new Set<string>();
    for (const unit of [...snapshot.heroes, ...snapshot.enemies]) {
      const figure = positions.get(unit.id);
      if (unit.health.dead || !figure?.visible) continue;
      shown.add(unit.id);
      const kinds = new Set(unitStatuses(unit));
      for (const kind of STATUS_KINDS) {
        const key = `${unit.id}:${kind}`, until = this.previews.get(key);
        if (until !== undefined && until <= time) this.previews.delete(key);
        else if (until !== undefined) kinds.add(kind);
      }
      const height = this.heights.get(unit.id) ?? 2;
      for (const kind of kinds) {
        const key = `${unit.id}:${kind}`; held.add(key);
        let status = this.active.get(key);
        // A status that returns while its last cue is still fading starts over as a fresh one.
        if (status?.ended !== undefined) { status.ended = undefined; status.started = time; }
        if (!status) {
          status = { kind, unitId: unit.id, group: createStatusVisual(kind, unit.radius, height), started: time, lift: 0 };
          this.scene.add(status.group); this.active.set(key, status);
        }
        status.group.position.copy(figure.position);
        // Stars ride the head, which lies near the ground on a Prone unit.
        if (kind === 'dazed') status.group.position.y += kinds.has('prone') ? height * .3 : height + .12;
        else if (kind === 'blinded') {
          // Round the eyes, wherever the head is in its pose; a body without a head bone takes it near the top.
          const head = figure.getObjectByName('head');
          if (head) head.getWorldPosition(status.group.position).y += .07; else status.group.position.y += kinds.has('prone') ? height * .25 : height * .88;
        }
        else if (kind === 'prone' || kind === 'running' || kind === 'slowed') status.group.position.y += .05;
        status.lift = status.group.position.y - figure.position.y;
        updateStatusVisual(status.group, kind, Math.max(0, time - status.started), time, ppm);
      }
    }
    for (const [key, status] of this.active) {
      if (held.has(key)) continue;
      // A dead or unseen unit drops its cues at once; a status that simply ended fades away where it is.
      if (status.ended === undefined && shown.has(status.unitId)) status.ended = time;
      const out = status.ended === undefined ? 1 : (time - status.ended) / FADE_OUT;
      if (!shown.has(status.unitId) || out >= 1 || out < 0) { disposeEffect(status.group); this.active.delete(key); continue; }
      status.group.position.copy(positions.get(status.unitId)!.position).y += status.lift;
      updateStatusVisual(status.group, status.kind, Math.max(0, time - status.started), time, ppm, 1 - out);
    }
  }
  clear() {
    for (const status of this.active.values()) disposeEffect(status.group);
    this.active.clear(); this.previews.clear();
  }
  dispose() { this.clear(); }
}
